import React, { useState, useEffect, useMemo } from 'react';
import { ocrConfigService, domainService } from '../../services';
import { OCRConfig, Domain } from '../../types';
import { OcrConfigModal } from '../../components/admin/OcrConfigModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';
import { UsagesModal } from '../../components/catalog/UsagesModal';
import { useCatalogUsages } from '../../components/catalog/useCatalogUsages';

const PSM_LABELS: Record<number, string> = {
  0: 'OSD', 1: 'Auto+OSD', 2: 'Auto', 3: 'Auto complet', 4: 'Colonne',
  5: 'Bloc vert.', 6: 'Bloc unif.', 7: 'Ligne', 8: 'Mot', 9: 'Cercle',
  10: 'Caract.', 11: 'Sparse', 12: 'Sparse+OSD', 13: 'Brut',
};

const OEM_LABELS: Record<number, string> = {
  0: 'Legacy', 1: 'LSTM', 2: 'Legacy+LSTM', 3: 'Auto',
};

export const OcrConfigsPage: React.FC = () => {
  const [configs, setConfigs] = useState<OCRConfig[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState<OCRConfig | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);

  const usages = useCatalogUsages('ocr-configs');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configsData, domainsData] = await Promise.all([
        ocrConfigService.list(),
        domainService.list()
      ]);
      setConfigs(configsData);
      setDomains(domainsData.filter(d => d.is_active));
    } catch (error) {
      console.error('Error loading OCR configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (config: OCRConfig) => {
    if (!confirm(`Supprimer la configuration "${config.name}" ?`)) return;
    await usages.handleDeleteWithGuard(
      config.id,
      config.name,
      () => ocrConfigService.delete(config.id),
      () => loadData()
    );
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedConfig(null);
    setDuplicateMode(false);
  };

  const handleSave = () => {
    handleModalClose();
    loadData();
  };

  const columns: FilterableColumnDef<OCRConfig>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (c) => c.name,
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (c) => c.version,
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      render: (c) => c.domain,
    },
    {
      key: 'dpi',
      header: 'DPI',
      sortable: true,
      sortValueFn: (c) => String(c.dpi).padStart(6, '0'),
      render: (c) => c.dpi,
    },
    {
      key: 'psm',
      header: 'PSM',
      filterable: true,
      filterValueFn: (c) => PSM_LABELS[c.psm] || String(c.psm),
      render: (c) => <span title={`PSM ${c.psm}`}>{PSM_LABELS[c.psm] || String(c.psm)}</span>,
    },
    {
      key: 'oem',
      header: 'OEM',
      filterable: true,
      filterValueFn: (c) => OEM_LABELS[c.oem] || String(c.oem),
      render: (c) => <span title={`OEM ${c.oem}`}>{OEM_LABELS[c.oem] || String(c.oem)}</span>,
    },
    {
      key: 'language',
      header: 'Langue',
      filterable: true,
      render: (c) => c.language,
    },
    {
      key: 'usages',
      header: 'Activé dans',
      sortable: true,
      sortValueFn: (c) => String(c.tenants_count ?? 0).padStart(6, '0'),
      render: (c) => (
        <button
          onClick={() => usages.openModal(c.id, c.name)}
          className="text-blue-600 hover:underline text-sm"
        >
          {c.tenants_count ?? 0} tenants
        </button>
      ),
    },
    {
      key: 'created_by',
      header: 'Créé par',
      filterable: true,
      filterValueFn: (c) => c.created_by_name ? [c.created_by_name] : [],
      render: (c) => <span className="text-sm text-gray-500">{c.created_by_name || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <div className="flex gap-2">
          <button onClick={() => { setEditMode(true); setDuplicateMode(false); setSelectedConfig(c); setShowModal(true); }} className="text-blue-600 hover:underline text-sm">
            Modifier
          </button>
          <button onClick={() => { setEditMode(false); setDuplicateMode(true); setSelectedConfig(c); setShowModal(true); }} className="text-teal-600 hover:underline text-sm">
            Dupliquer
          </button>
          <button
            onClick={() => handleDelete(c)}
            className="text-sm text-red-600 hover:underline"
            title="Supprimer"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ], [usages]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Configurations OCR</h1>
        <button
          onClick={() => { setEditMode(false); setSelectedConfig(null); setShowModal(true); }}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouvelle
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-ocr-configs"
          paginated
          data={configs}
          columns={columns}
          rowKey={(c) => c.id}
          defaultSort={{ key: 'name', direction: 'asc' }}
          noDataMessage="Aucune configuration OCR trouvée"
          emptyMessage="Aucune configuration ne correspond aux filtres"
        />
      )}

      {showModal && (
        <OcrConfigModal
          config={editMode ? selectedConfig : null}
          duplicateFrom={duplicateMode ? selectedConfig : null}
          domains={domains}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}

      <UsagesModal
        open={usages.modal.open}
        catalogType="ocr-configs"
        objectId={usages.modal.objectId}
        objectLabel={usages.modal.objectLabel}
        onClose={usages.closeModal}
      />
    </PageContainer>
  );
};
